import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Eye, EyeOff, Trash2, ExternalLink, Globe } from "lucide-react";

/** Grid of every customer-owned hosted_sites doc with inline status toggle. */
export default function CmsWebsites() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ all: 0, published: 0, draft: 0, deleted: 0 });
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get("/admin/cms/websites", { params: { status, limit: 200 } });
      setItems(r.data?.items || []);
      setCounts(r.data?.counts || counts);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load websites");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const setState = async (sid, newStatus) => {
    try {
      await http.patch(`/admin/cms/websites/${sid}/status`, { status: newStatus });
      toast.success(`Website ${newStatus}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="max-w-6xl" data-testid="cms-websites">
      <Link to="/admin/cms" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to CMS home
      </Link>
      <h1 className="text-2xl font-semibold text-slate-900">Customer Websites</h1>
      <p className="mt-1 text-sm text-slate-500">
        Every business-owned website in one grid. {counts.published} published · {counts.draft} draft · {counts.all} total.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {["all", "published", "draft", "deleted"].map((s) => (
          <button
            key={s}
            data-testid={`cms-websites-filter-${s}`}
            onClick={() => setStatus(s)}
            className={`rounded-full border px-3 py-1 text-xs ${status === s ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600"}`}
          >
            {s} ({counts[s] ?? 0})
          </button>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {items.map((w) => (
          <div key={w.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`cms-website-${w.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <Globe className="h-4 w-4 text-slate-400" /> {w.name || w.title || w.slug}
                </div>
                <div className="mt-0.5 truncate text-xs text-slate-500">/{w.slug}</div>
                <div className="mt-1 text-[11px] text-slate-500">
                  Theme <span className="font-medium text-slate-700">{w.theme || "default"}</span> · Template <span className="font-medium text-slate-700">{w.template || "generic"}</span>
                </div>
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                w.status === "published" ? "bg-emerald-50 text-emerald-700" :
                w.status === "deleted"   ? "bg-rose-50 text-rose-700" :
                                            "bg-amber-50 text-amber-700"
              }`}>{w.status || "draft"}</span>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              <a
                href={`/sites/${w.slug}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:border-emerald-300 hover:bg-emerald-50"
              >
                <ExternalLink className="h-3 w-3" /> View
              </a>
              {w.status !== "published" && (
                <Button size="sm" onClick={() => setState(w.id, "published")} data-testid={`cms-website-publish-${w.id}`} className="h-7 bg-emerald-600 text-white hover:bg-emerald-700">
                  <Eye className="mr-1 h-3 w-3" /> Publish
                </Button>
              )}
              {w.status === "published" && (
                <Button size="sm" variant="outline" onClick={() => setState(w.id, "unpublished")} data-testid={`cms-website-unpublish-${w.id}`} className="h-7">
                  <EyeOff className="mr-1 h-3 w-3" /> Unpublish
                </Button>
              )}
              {w.status !== "deleted" && (
                <Button size="sm" variant="outline" onClick={() => setState(w.id, "deleted")} className="h-7 border-rose-200 text-rose-600 hover:bg-rose-50">
                  <Trash2 className="mr-1 h-3 w-3" /> Delete
                </Button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && !loading && (
          <div className="col-span-full rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            No websites match this filter.
          </div>
        )}
      </div>
    </div>
  );
}
